import { Heading, HeadingLevel } from "@ariakit/react";
import {
  DownloadLink,
  ListItemContainer,
  ListItemContent,
  ListItemHeading,
  ListItemSummary,
  ListView,
  trackEvent,
  useAppConfig,
  useIsInstalled,
} from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import { type OwnedProduct } from "@indietabletop/types";
import { Link, useLocation, useParams } from "wouter";
import { href } from "../../App/href.tsx";
import { useGetUnlocks } from "../../CheckAccess/context.ts";
import { useInvokeClient } from "../../client.ts";
import { FailureView } from "../../FailureView/FailureView.tsx";
import { FathomEvent } from "../../fathom.ts";
import { FeatureEnum } from "../../fmt.ts";
import { LoadingView } from "../../LoadingView/LoadingView.tsx";
import {
  Page,
  SectionContainer,
  SectionHeading,
  SectionParagraph,
  ViewContent,
} from "../Layout/Layout.tsx";
import { overrides, page, section, socials } from "./style.css.ts";
import contentUrl from "./unlocked.png";

type SocialLink = {
  href: string;
  action: string;
  description: string;
  imgSrc: string;
  eventName: FathomEvent;
};

function SocialLink(props: {
  href: string;
  action: string;
  description: string;
  logoSrc: string;
  event: FathomEvent;
}) {
  return (
    <div className={socials.item}>
      <a
        href={props.href}
        target="_blank"
        rel="noreferrer noopener"
        onClick={() => void trackEvent(props.event)}
        style={{ display: "contents" }}
      >
        <img src={props.logoSrc} alt="" className={socials.icon} />
      </a>

      <div className={socials.info}>{props.description}</div>

      <a
        href={props.href}
        target="_blank"
        rel="noreferrer noopener"
        onClick={() => void trackEvent(props.event)}
        className={socials.action}
      >
        {props.action}
      </a>
    </div>
  );
}

function Socials(props: { links: SocialLink[] }) {
  return (
    <div className={socials.container}>
      {props.links.map((link) => {
        return (
          <SocialLink
            key={link.href}
            href={link.href}
            logoSrc={link.imgSrc}
            description={link.description}
            action={link.action}
            event={link.eventName}
          />
        );
      })}
    </div>
  );
}

function CheckoutSessionView(props: {
  session: {
    products: OwnedProduct[];
    links: SocialLink[];
    orderRef: string;
  };
}) {
  const { products, orderRef, links } = props.session;

  // Re-run the check access call to make sure that if new unlocks were
  // added, they show up in the app. This is important in case Stripe was too
  // slow notifying us that a purchase happened via webhooks and it was the
  // session check call that resulted in the purchased being processed.
  useGetUnlocks({ gameCode: "greathelm" });

  const unlocks = new Set(
    products
      .flatMap((product) => {
        return (
          product.unlocks
            ?.filter((u) => u.gameCode === "greathelm")
            .map((u) => u.feature) ?? []
        );
      })
      .filter((feature) => FeatureEnum.has(feature))
      .map((feature) => FeatureEnum.get(feature)),
  );

  return (
    <ViewContent blockVariant="shrink" inlineVariant="narrow">
      <SectionContainer>
        <CheckoutHeader orderRef={orderRef} />

        <HeadingLevel>
          <div className={page.sections}>
            <DownloadsSection products={products} />

            <UnlocksSection unlocks={unlocks} />

            <ReferralsSection links={links} />
          </div>
        </HeadingLevel>
      </SectionContainer>
    </ViewContent>
  );
}

function CheckoutHeader(props: { orderRef: string }) {
  const { orderRef } = props;

  return (
    <div className={page.header}>
      <div className={page.overline}>Payment received! Thanks!</div>
      <Heading className={page.heading}>What next?</Heading>
      <div className={page.orderInfo}>
        Your payment has been processed successfully.
        <br />
        Order reference: <span className={page.orderRef}>{orderRef}</span>
      </div>
    </div>
  );
}

function DownloadsSection(props: { products: OwnedProduct[] }) {
  const { products } = props;

  const someMissingDownloads = products.some((p) => !p.downloadUrl);

  return (
    <div>
      <SectionHeading>Download your stuff</SectionHeading>

      <SectionParagraph>
        You are all set to download your new stuff! Your purchases are saved to
        your account, so you can always get back to them later from the app.
      </SectionParagraph>

      {someMissingDownloads && (
        <SectionParagraph>
          <strong>⚠️ Warning</strong>: Some digital products below are missing
          download links. This is probably an issue with our service. Our team
          has been automatically notified, but if you don&apos;t hear from us
          soon, please get in touch with support@indietabletop.club. Sorry about
          this!
        </SectionParagraph>
      )}

      <ListView className={overrides.downloadsList}>
        {products.map((product) => {
          return (
            <ListItemContainer key={product.id}>
              <ListItemContent>
                <ListItemHeading className={overrides.downloadHeading}>
                  {product.name}
                </ListItemHeading>

                <ListItemSummary className={overrides.downloadSummary}>
                  {product.downloadFileVersion && (
                    <span>Version {product.downloadFileVersion}</span>
                  )}
                  <span>{product.downloadFileSize}</span>
                </ListItemSummary>
              </ListItemContent>

              {product.downloadUrl && (
                <DownloadLink
                  className={overrides.downloadButton}
                  href={product.downloadUrl}
                >
                  Download
                </DownloadLink>
              )}
            </ListItemContainer>
          );
        })}
      </ListView>
    </div>
  );
}

function UnlocksSection(props: { unlocks: Set<string> }) {
  const { unlocks } = props;

  const isInstalled = useIsInstalled();
  const { fmt } = useAppConfig();
  const [path] = useLocation();

  if (unlocks.size === 0) {
    return null;
  }

  return (
    <div className={section.container}>
      <div>
        <SectionHeading>Build your warband</SectionHeading>

        <SectionParagraph>
          Your purchase has unlocked in-app access to{" "}
          {fmt.conjunction(Array.from(unlocks))}. All related content will now
          be visible all across the app.
        </SectionParagraph>

        {!isInstalled && (
          <SectionParagraph>
            <em>
              Pssst! Did you know you can install the app on any modern device
              to make it extra easy to use?{" "}
              <Link
                href={`~/docs/help/installation?${new URLSearchParams({ backTo: `~${path}` })}`}
                className={interactiveText}
              >
                Learn more
              </Link>
              .
            </em>
          </SectionParagraph>
        )}

        <Link className={section.builderLink} href={href("~/builder")}>
          Start building!
        </Link>
      </div>

      <img src={contentUrl} alt="" className={section.illustration} />
    </div>
  );
}

function ReferralsSection(props: { links: SocialLink[] }) {
  const { links } = props;

  if (links.length === 0) {
    return null;
  }

  return (
    <div>
      <div>
        <SectionHeading>Join the community</SectionHeading>

        <SectionParagraph>
          Want to hang out with other Greathelm players? Chat about the game?
          Show off your warbands? Join Malev&apos;s Greathelm communities!
        </SectionParagraph>
      </div>

      <div className={section.externalLinks}>
        <Socials links={props.links} />
      </div>
    </div>
  );
}

function useGetCheckoutSession(sessionId: string) {
  const [result] = useInvokeClient("getCheckoutSession", [sessionId]);
  return result.mapSuccess((value) => ({
    // Set these first to allow overrides once these come from the endpoint
    links: [],

    ...value,
  }));
}

export function CheckoutSuccessPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const result = useGetCheckoutSession(sessionId);

  if (result.isPending) {
    return (
      <Page title="" backHref="~/">
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page title="" backHref="~/">
        <FailureView
          failure={result.failure}
          // messages={{
          //   409: {
          //     title: "Payment not yet processed",
          //     description: `Our payments provider has not yet confirmed that a payment was received. Please check back later, or contact support if this issue persists.`,
          //   },
          // }}
        />
      </Page>
    );
  }

  return (
    <Page backHref="~/" title="Success">
      <CheckoutSessionView session={result.value} />
    </Page>
  );
}
